/**
 * Handles session arrival when offline.
 * Returns an Observable that optimistically updates Redux and enqueues for sync.
 *
 * @author Milton Filho
 */
import { Observable, of, concat } from 'rxjs';
import { AnyPayloadAction, Appointment } from 'types/types';
import { StateObservable } from 'redux-observable';
import { GlobalState } from 'reducers';
import { updateAppointmentSuccess } from 'modules/appointments/actions';
import { enqueueOfflineItem } from 'modules/offlineSync/actions';
import { OfflineActionKind, OfflineQueueItem } from 'modules/offlineSync/types';
import { denormalizeAppointment } from 'modules/appointments/utils/normalizeAppointment';
import { randomUUID } from 'utils/crypto/randomUUID';

export const handleOfflineArrival = (
  appointmentId: number,
  updates: Partial<Appointment>,
  state$: StateObservable<GlobalState>,
): Observable<AnyPayloadAction> => {
  const existingAppointment =
    state$.value.appointments.normalized[String(appointmentId)];

  const arrivedAt = new Date().toISOString();
  const optimisticAppointment: Appointment = {
    ...existingAppointment,
    ...updates,
    arrivedAt,
  };

  const queueItem: OfflineQueueItem = {
    id: randomUUID(),
    kind: OfflineActionKind.StartSession,
    payload: denormalizeAppointment({ id: appointmentId, ...updates }),
    tempAppointmentIds: [appointmentId],
    createdAt: new Date().toISOString(),
  };

  return concat(
    of(enqueueOfflineItem(queueItem)),
    of(updateAppointmentSuccess(optimisticAppointment)),
  );
};
